
import {
  IconChevronDown,
  IconSearch,
  IconPin,
  IconPinFilled,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { FormInput } from "../../Inputs/FormInput";
import { Event } from "@/types/Event";
import { ScopedLoadingSpinner } from "../../LoadingSpinner/ScopedLoadingSpinner";
import { useEventSwitcher } from "./useEventSwitcher";
import { useTranslations } from "next-intl";
import { useRef, useLayoutEffect, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { addOutsidePressListener } from "@/lib/slotting/outsidePress";

type EventSwitcherProps = {
  eventSelected: boolean;
  eventId?: string;
  initialItem: string;
  eventsProp: {
    validEvents: Event[];
    validPinnedEvents: Event[];
    validRecentEvents: Event[];
  };
};

export const EventSwitcher: React.FC<EventSwitcherProps> = ({
  eventId,
  initialItem,
  eventsProp,
  eventSelected,
}) => {
  const t = useTranslations("appbar");
  const {
    setIsDropdownOpen,
    selectedItem,
    isDropdownOpen,
    sortedItems,
    handleSelect,
    togglePin,
    loadingNavigation,
    search,
    setSearch,
    inputRef,
    totalQuantityEvents,
    getHrefForEvent,
  } = useEventSwitcher({
    eventId,
    initialItem,
    eventsProp,
  });

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Touch has no mouseleave: close on any press outside trigger + panel
  // (shared dual pointerdown+mousedown helper — never re-implement with
  // capture or mousedown-only).
  useEffect(() => {
    if (!isDropdownOpen) return;
    return addOutsidePressListener((e) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setIsDropdownOpen(false);
    });
  }, [isDropdownOpen, setIsDropdownOpen]);

  const canOpen =
    (eventSelected && totalQuantityEvents > 1) || !eventSelected;

  // Re-anchor on scroll/resize so the portal follows the trigger.
  useLayoutEffect(() => {
    if (!isDropdownOpen) return;

    let rafId = 0;
    const recompute = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      // Clamp the centered menu inside the viewport: the trigger sits
      // right-of-center in the appbar, so on narrow screens a naive
      // center-anchor pushes the 90vw panel past the right edge.
      const menuWidth =
        window.innerWidth >= 640 ? 400 : window.innerWidth * 0.9;
      const margin = 8;
      const center = rect.left + rect.width / 2;
      const clamped = Math.min(
        Math.max(center, margin + menuWidth / 2),
        window.innerWidth - margin - menuWidth / 2,
      );
      setDropdownPos({
        top: rect.bottom,
        left: clamped,
        width: rect.width,
      });
    };
    const scheduleRecompute = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        recompute();
      });
    };

    recompute();
    window.addEventListener("scroll", scheduleRecompute, true);
    window.addEventListener("resize", scheduleRecompute);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", scheduleRecompute, true);
      window.removeEventListener("resize", scheduleRecompute);
    };
  }, [isDropdownOpen]);

  const toggleOpen = () => {
    if (!canOpen) return;
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canOpen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleOpen();
    } else if (e.key === "Escape" && isDropdownOpen) {
      e.preventDefault();
      setIsDropdownOpen(false);
    }
  };

  return (
    <div
      ref={triggerRef}
      // Below sm the trigger is the only thing in the bar that may give way:
      // every icon in the controls row is shrink-0 and the page clips its
      // right edge (html overflow-x hidden), so with a long event name the
      // avatar — the only logout path in phone chrome — went off-screen. A
      // fixed vw cap cannot fit both the event page (menu button) and the
      // dashboard (70px logo). Shrinkable up to md, not sm: with the inner
      // min-w-100 fixed from 640px, the onboarding pill's ring was clipped at
      // 640–666px (en) and, for the longest pill labels on the dashboard, at
      // 768–781px — iPad portrait — hence the smaller md:min-w-80 minimum
      // until lg. Below the breakpoint only the invisible hit area and the
      // name's centring inside it change (no border; the listbox is clamped).
      className="min-w-0 shrink md:flex-none relative"
      role="button"
      tabIndex={canOpen ? 0 : -1}
      aria-haspopup="listbox"
      aria-expanded={isDropdownOpen}
      aria-controls="event-switcher-listbox"
      onClick={toggleOpen}
      onKeyDown={handleTriggerKeyDown}
      // Pointer events carry the REAL pointerType per interaction (media
      // queries only describe the primary device — wrong on touch-laptops).
      // A finger tap fires pointerenter with type "touch" and must NOT
      // pre-open: the tap's click would toggle it straight back closed
      // (and the open used to autofocus a 12px input → iOS page zoom).
      onPointerEnter={(e) => {
        if (canOpen && e.pointerType !== "touch") setIsDropdownOpen(true);
      }}
      onPointerLeave={(e) => {
        if (canOpen && e.pointerType !== "touch") setIsDropdownOpen(false);
      }}
    >
      <div
        className={`flex flex-row justify-center items-center pl-3 pr-2 py-1 min-w-0 md:min-w-80 lg:min-w-100 max-w-[60vw] sm:max-w-100 ${
          canOpen ? "cursor-pointer" : ""
        }`}
      >
        <p className="body-2-medium text-center truncate">
          {selectedItem?.length > 60
            ? `${selectedItem.slice(0, 57)}...`
            : selectedItem}
        </p>
        {canOpen ? (
          <IconChevronDown
            className={`ml-1 shrink-0 transition-transform duration-300 ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
            size={20}
          />
        ) : null}
      </div>

      {isDropdownOpen &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-50 w-[90vw] sm:w-100"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              transform: "translateX(-50%)",
            }}
            // React portals bubble synthetic events to the REACT-tree parent —
            // which here is the trigger div with onClick={toggleOpen}. Without
            // this stop, tapping the search input closed the whole dropdown.
            // Item selection still closes via useDropdown's handleSelect.
            onClick={(e) => e.stopPropagation()}
            onPointerEnter={(e) => {
              if (e.pointerType !== "touch") setIsDropdownOpen(true);
            }}
            onPointerLeave={(e) => {
              if (e.pointerType !== "touch") setIsDropdownOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.stopPropagation();
                setIsDropdownOpen(false);
              }
            }}
          >
            <div className="pt-1">
              <div className="surface-primary shadow-lg rounded-lg border pb-3 pl-3 h-70 justify-between flex flex-col outline-quaternary">
                {sortedItems?.length <= 0 ? (
                  <div className="flex-1 body-3-medium p-2">{t("noResults")}</div>
                ) : (
                  <ul
                    id="event-switcher-listbox"
                    role="listbox"
                    className="flex-1 overflow-y-auto custom-scrollbar pt-3 pr-3 shrink-0"
                  >
                    {sortedItems.map((item, index) => (
                      <li
                        key={index}
                        role="option"
                        aria-selected={false}
                        className="mb-1"
                      >
                        <Link
                          to={getHrefForEvent(item.eventId)}
                          onClick={(e) => {
                            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                            e.preventDefault();
                            handleSelect(item.eventId, false);
                          }}
                          className="flex justify-between items-center p-2 rounded-lg body-3-medium hover:surface-tertiary cursor-pointer group"
                        >
                          <p className="body-3-regular truncate flex-1 mr-2">
                            {item.name?.length > 60
                              ? `${item.name.slice(0, 57)}...`
                              : item.name}
                          </p>
                          <span
                            className="shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              togglePin(item.eventId);
                            }}
                          >
                            {item.pinned ? (
                              <IconPinFilled size={16} className="font-primary" />
                            ) : (
                              <IconPin
                                size={16}
                                className="font-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              />
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <FormInput
                  className="pr-3 shrink-0"
                  ref={inputRef}
                  icon={IconSearch}
                  margin=""
                  iconPaddingLeft="pl-2"
                  iconPaddingRight="pl-1"
                  height="h-8"
                  iconSize={16}
                  fontSize="body-3-regular"
                  placeholder={t("searchEvents")}
                  id={"search"}
                  search
                  value={search}
                  onChangeAction={(value) => setSearch(value)}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
      {loadingNavigation ? <ScopedLoadingSpinner /> : null}
    </div>
  );
};
